import { CSS_PX_PER_PT, firstFamily } from '@messenger/consts'
import {
  clampEditorLetterSpacingPt,
  clampEditorStrokeWidthPt,
  editorColorSpecFromCss,
  editorPaintedSizePt,
  editorStylingInputSetsAnything,
  mergeEditorBlockStyling,
  mergeEditorCharacterStyling,
  quantizeEditorAuthoredPt,
} from '@messenger/lotus'
import { TINYMCE_BASE_FONT_FAMILY } from '@messenger/lotus/fonts'
import type {
  EditorBlockStylingInput,
  EditorCharacterStylingInput,
} from '@messenger/lotus/row-store'
import { asStored } from '../editorTextBoxSave'
import { inlineDeclarations, isHiddenElement, tinyMceText } from './parse'

type ColorSpec = NonNullable<EditorCharacterStylingInput['color']>
type Decoration = NonNullable<EditorCharacterStylingInput['textDecoration']>

/**
 * TinyMCE's root size: the `.mce-content-body` rule in
 * `apps/mdl-frontend/src/styles/tinyMCEStyles.ts` sets 14px, and a new
 * TinyMCE box's text carries no size of its own.
 */
const TINYMCE_ROOT_FONT_SIZE_PX = 14

/** Chrome's `medium`, in px: the document root a `rem` counts from. */
const MEDIUM_SIZE_PX = 16

/**
 * Chrome's absolute-size keywords, in px. The lookup tables here are maps, so
 * a stored value that names an object property (`constructor`) finds nothing.
 */
const KEYWORD_SIZE_PX: ReadonlyMap<string, number> = new Map([
  ['xx-small', 9],
  ['x-small', 10],
  ['small', 13],
  ['medium', MEDIUM_SIZE_PX],
  ['large', 18],
  ['x-large', 24],
  ['xx-large', 32],
  ['xxx-large', 48],
])

/** The legacy `<font size="1..7">` steps, in px. */
const FONT_TAG_SIZE_PX: ReadonlyMap<string, number> = new Map([
  ['1', 10],
  ['2', 13],
  ['3', 16],
  ['4', 18],
  ['5', 24],
  ['6', 32],
  ['7', 48],
])

/** CSS-wide keywords: they name no value of the element's own. */
const CSS_WIDE_KEYWORDS: ReadonlySet<string> = new Set([
  'inherit',
  'initial',
  'unset',
  'revert',
  'revert-layer',
])

/**
 * A size as a run stores it: on the 0.1 pt grid an authored size takes, and
 * within the band a box paints sizes in.
 */
const sizeInput = (pt: number): number =>
  editorPaintedSizePt(quantizeEditorAuthoredPt(pt), 1)

/** What TinyMCE painted unstyled text in: the outermost layer of every fold. */
export const TINYMCE_ROOT_CHARACTER_STYLING: EditorCharacterStylingInput = {
  fontFamily: firstFamily(TINYMCE_BASE_FONT_FAMILY),
  fontSize: sizeInput(TINYMCE_ROOT_FONT_SIZE_PX / CSS_PX_PER_PT),
}

/** A fold's size in pt; every fold starts from TinyMCE's root, which sets one. */
export const fontSizeOf = (styling: EditorCharacterStylingInput): number =>
  styling.fontSize ?? (TINYMCE_ROOT_CHARACTER_STYLING.fontSize as number)

const numberWithUnit = (
  value: string,
): { n: number; unit: string } | undefined => {
  // No two digit runs can trade digits, so a value of any length is read in
  // one pass.
  const match = /^(-?(?:\d+(?:\.\d+)?|\.\d+))([a-z%]*)$/i.exec(value.trim())
  if (!match) return undefined
  const n = Number(match[1])
  return Number.isFinite(n) ? { n, unit: match[2].toLowerCase() } : undefined
}

/** A pt or px length in points; a bare number is no length, as the browser read it. */
const lengthPt = (value: string): number | undefined => {
  const parsed = numberWithUnit(value)
  if (parsed?.unit === 'pt') return parsed.n
  if (parsed?.unit === 'px') return parsed.n / CSS_PX_PER_PT
  return undefined
}

/** A size with a unit, in pt: an em or a percentage of the inherited size, a rem of the document root's, or a length. */
const unitSizePt = (value: string, inheritedPt: number): number | undefined => {
  const parsed = numberWithUnit(value)
  if (parsed === undefined) return undefined
  switch (parsed.unit) {
    case 'em':
      return inheritedPt * parsed.n
    case '%':
      return (inheritedPt * parsed.n) / 100
    case 'rem':
      return (MEDIUM_SIZE_PX / CSS_PX_PER_PT) * parsed.n
    default:
      return lengthPt(value)
  }
}

const fontSizePt = (value: string, inheritedPt: number): number | undefined => {
  const v = value.trim().toLowerCase()
  const keyword = KEYWORD_SIZE_PX.get(v)
  if (keyword !== undefined) return keyword / CSS_PX_PER_PT
  if (v === 'smaller') return inheritedPt / 1.2
  if (v === 'larger') return inheritedPt * 1.2
  const pt = unitSizePt(v, inheritedPt)
  return pt !== undefined && pt > 0 ? pt : undefined
}

const fontWeightInput = (value: string): string | undefined => {
  const v = value.trim().toLowerCase()
  if (v === 'bold' || v === 'bolder') return '700'
  if (v === 'normal') return '400'
  if (v === 'lighter') return '300'
  const n = Number(v)
  return v !== '' && Number.isFinite(n) && n >= 1 && n <= 1000
    ? String(n)
    : undefined
}

const fontStyleInput = (
  value: string,
): EditorCharacterStylingInput['fontStyle'] => {
  const keyword = value.trim().toLowerCase().split(/\s+/)[0]
  return keyword === 'italic' || keyword === 'oblique' || keyword === 'normal'
    ? keyword
    : undefined
}

/** One line of a decoration list, underline first: a run holds one. */
const decorationInput = (value: string): Decoration | undefined => {
  const words = new Set(value.trim().toLowerCase().split(/\s+/))
  for (const line of ['underline', 'line-through', 'overline'] as const)
    if (words.has(line)) return line
  return words.has('none') ? 'none' : undefined
}

const textCaseInput = (
  value: string,
): EditorCharacterStylingInput['textCase'] => {
  const v = value.trim().toLowerCase()
  return v === 'uppercase' ||
    v === 'lowercase' ||
    v === 'capitalize' ||
    v === 'none'
    ? v
    : undefined
}

const colorInput = (value: string): ColorSpec | undefined =>
  CSS_WIDE_KEYWORDS.has(value.trim().toLowerCase())
    ? undefined
    : editorColorSpecFromCss(value)

const letterSpacingInput = (
  value: string,
  fontSizePtHere: number,
): number | undefined => {
  if (value.trim().toLowerCase() === 'normal') return 0
  const parsed = numberWithUnit(value)
  const pt = parsed?.unit === 'em' ? parsed.n * fontSizePtHere : lengthPt(value)
  return pt === undefined ? undefined : clampEditorLetterSpacingPt(pt)
}

/** `-webkit-text-stroke: <width> <color>`, in either order. */
const strokeInput = (value: string): { width?: number; color?: ColorSpec } => {
  const words = value.trim().split(/\s+/)
  const widthAt = words.findIndex((word) => lengthPt(word) !== undefined)
  if (widthAt < 0) return {}
  const width = lengthPt(words[widthAt]) as number
  if (width < 0) return {}
  const color = words.filter((_, i) => i !== widthAt).join(' ')
  return {
    width: clampEditorStrokeWidthPt(width),
    ...(color === '' ? {} : { color: editorColorSpecFromCss(color) }),
  }
}

// eslint-disable-next-line no-control-regex
const CONTROL_CHARACTERS = /[\u0000-\u001f\u007f-\u009f]/

const familyInput = (value: string): string | undefined => {
  const family = firstFamily(value).trim()
  return family === '' ||
    CONTROL_CHARACTERS.test(family) ||
    CSS_WIDE_KEYWORDS.has(family.toLowerCase())
    ? undefined
    : family
}

/** What a formatting tag means, before the element's own style overrides it. */
const TAG_STYLING: ReadonlyMap<string, EditorCharacterStylingInput> = new Map<
  string,
  EditorCharacterStylingInput
>([
  ['strong', { fontWeight: '700' }],
  ['b', { fontWeight: '700' }],
  ['em', { fontStyle: 'italic' }],
  ['i', { fontStyle: 'italic' }],
  ['u', { textDecoration: 'underline' }],
  ['ins', { textDecoration: 'underline' }],
  ['s', { textDecoration: 'line-through' }],
  ['strike', { textDecoration: 'line-through' }],
  ['del', { textDecoration: 'line-through' }],
])

/** An element's declarations, a `<font>` tag's attributes read as the declarations they stand for. */
const declarationsOf = (el: Element): Map<string, string> => {
  const css = inlineDeclarations(el)
  if (el.tagName !== 'FONT') return css
  const color = el.getAttribute('color')
  if (color && !css.has('color')) css.set('color', color)
  const face = el.getAttribute('face')
  if (face && !css.has('font-family')) css.set('font-family', face)
  const size = FONT_TAG_SIZE_PX.get(el.getAttribute('size')?.trim() ?? '')
  if (size !== undefined && !css.has('font-size'))
    css.set('font-size', `${size}px`)
  return css
}

/** The character styling an element's tag and own style contribute, spelled as the toolbar spells it. */
const characterDelta = (
  el: Element,
  parent: EditorCharacterStylingInput,
): EditorCharacterStylingInput => {
  const css = declarationsOf(el)
  const inheritedPt = fontSizeOf(parent)
  const delta: EditorCharacterStylingInput = {
    ...TAG_STYLING.get(el.tagName.toLowerCase()),
  }
  const set = <K extends keyof EditorCharacterStylingInput>(
    key: K,
    value: EditorCharacterStylingInput[K] | undefined,
  ): void => {
    if (value !== undefined) delta[key] = value
  }
  const read = (property: string): string | undefined => css.get(property)

  const family = read('font-family')
  if (family !== undefined) set('fontFamily', familyInput(family))
  const size = read('font-size')
  const sizePt = size === undefined ? undefined : fontSizePt(size, inheritedPt)
  if (sizePt !== undefined) set('fontSize', sizeInput(sizePt))
  const weight = read('font-weight')
  if (weight !== undefined) set('fontWeight', fontWeightInput(weight))
  const style = read('font-style')
  if (style !== undefined) set('fontStyle', fontStyleInput(style))
  const color = read('color')
  if (color !== undefined && color.trim().toLowerCase() !== 'currentcolor')
    set('color', colorInput(color))
  const decoration = read('text-decoration-line') ?? read('text-decoration')
  if (decoration !== undefined)
    set('textDecoration', decorationInput(decoration))
  const decorationColor = read('text-decoration-color')
  if (decorationColor !== undefined)
    set('textDecorationColor', colorInput(decorationColor))
  const textCase = read('text-transform')
  if (textCase !== undefined) set('textCase', textCaseInput(textCase))
  const letterSpacing = read('letter-spacing')
  if (letterSpacing !== undefined)
    set(
      'letterSpacing',
      letterSpacingInput(letterSpacing, sizePt ?? inheritedPt),
    )
  const stroke = strokeInput(read('-webkit-text-stroke') ?? '')
  set('textStrokeWidth', stroke.width)
  set('textStrokeColor', stroke.color)
  const strokeWidth = read('-webkit-text-stroke-width')
  if (strokeWidth !== undefined) {
    const pt = lengthPt(strokeWidth)
    if (pt !== undefined && pt >= 0)
      set('textStrokeWidth', clampEditorStrokeWidthPt(pt))
  }
  const strokeColor = read('-webkit-text-stroke-color')
  if (strokeColor !== undefined) set('textStrokeColor', colorInput(strokeColor))
  const fill = read('-webkit-text-fill-color')
  if (fill !== undefined) set('textFillColor', colorInput(fill))
  return delta
}

/**
 * CSS draws an ancestor's decoration line under every descendant, which no
 * descendant can take off; a run holds one line, and underline wins.
 */
const decorationUnder = (
  parent: Decoration | undefined,
  child: Decoration,
): Decoration => {
  if (parent === undefined || parent === 'none') return child
  if (child === 'none' || parent === 'underline') return parent
  return child
}

/**
 * The styling of text inside `el`: its tag's meaning and its own style over
 * `parent`, innermost winning, merged the way the editor merges a toolbar
 * edit into a run.
 */
export const foldCharacterStyling = (
  parent: EditorCharacterStylingInput,
  el: Element,
): EditorCharacterStylingInput => {
  const delta = characterDelta(el, parent)
  if (delta.textDecoration !== undefined)
    delta.textDecoration = decorationUnder(
      parent.textDecoration,
      delta.textDecoration,
    )
  return mergeEditorCharacterStyling(parent, delta)
}

const textAlignInput = (
  value: string,
): EditorBlockStylingInput['textAlign'] => {
  const v = value.trim().toLowerCase()
  if (v === 'left' || v === 'start') return 'start'
  if (v === 'right' || v === 'end') return 'end'
  if (v === 'center' || v === 'justify') return v
  return undefined
}

/**
 * A line height as the multiple a block stores: a unitless value as it is, a
 * percentage or an em as its multiple, and a px or pt length over the
 * element's own font size.
 */
const lineHeightMultiple = (
  value: string,
  fontSizePtHere: number,
): number | undefined => {
  const parsed = numberWithUnit(value)
  if (parsed === undefined) return undefined
  switch (parsed.unit) {
    case '':
    case 'em':
      return parsed.n
    case '%':
      return parsed.n / 100
    case 'px':
    case 'pt':
      return (lengthPt(value) as number) / fontSizePtHere
    default:
      return undefined
  }
}

/** A line height as a block stores it; one too small to store is none, since a block's line height is positive. */
const lineHeightInput = (
  value: string,
  fontSizePtHere: number,
): number | undefined => {
  const multiple = lineHeightMultiple(value, fontSizePtHere)
  if (multiple === undefined) return undefined
  const stored = asStored(multiple)
  return stored > 0 ? stored : undefined
}

/**
 * The block styling inside `el`: its alignment and line height over
 * `parent`'s, merged as the editor merges a block edit, which pairs justify
 * with inter-word justification. `fontSizePt` is the size at `el`, which a
 * px or pt line height is a multiple of.
 */
export const foldBlockStyling = (
  parent: EditorBlockStylingInput | undefined,
  el: Element,
  fontSizePt: number,
): EditorBlockStylingInput => {
  const css = inlineDeclarations(el)
  const delta: EditorBlockStylingInput = {}
  const align = css.get('text-align')
  const textAlign = align === undefined ? undefined : textAlignInput(align)
  if (textAlign !== undefined) delta.textAlign = textAlign
  const height = css.get('line-height')
  const lineHeight =
    height === undefined ? undefined : lineHeightInput(height, fontSizePt)
  if (lineHeight !== undefined) delta.lineHeight = lineHeight
  return mergeEditorBlockStyling(parent, delta)
}

/** The styling a walk carries into an element: the character fold and the block styling around it. */
export type TinyMceContext = {
  character: EditorCharacterStylingInput
  block: EditorBlockStylingInput | undefined
}

export const TINYMCE_ROOT_CONTEXT: TinyMceContext = {
  character: TINYMCE_ROOT_CHARACTER_STYLING,
  block: undefined,
}

/** The context inside `el`: its character styling and its block styling folded over the context's. */
export const tinyMceContextInside = (
  context: TinyMceContext,
  el: Element,
): TinyMceContext => {
  const character = foldCharacterStyling(context.character, el)
  return {
    character,
    block: foldBlockStyling(context.block, el, fontSizeOf(character)),
  }
}

/** A block styling worth writing: undefined when it sets nothing. */
export const blockStylingOrNothing = (
  styling: EditorBlockStylingInput | undefined,
): EditorBlockStylingInput | undefined =>
  editorStylingInputSetsAnything(styling) ? styling : undefined

/** A styling's identity: equal stylings share it, whatever order their keys were set in. */
export const stylingKey = (styling: unknown): string =>
  JSON.stringify(styling, (_key, value: unknown) =>
    value !== null && typeof value === 'object' && !Array.isArray(value)
      ? Object.fromEntries(
          Object.entries(value as Record<string, unknown>).sort(([a], [b]) =>
            a < b ? -1 : a > b ? 1 : 0,
          ),
        )
      : value,
  )

/**
 * The styling a field run takes: the style every text leaf of its stored
 * value painted in, when they agree, since TinyMCE themes often style the
 * value on spans inside the field; a hidden part paints nothing and has no
 * say. Leaves that disagree keep `base`, the field element's own styling,
 * and `unanimous` says so.
 */
export const unanimousLeafStyling = (
  el: Element,
  base: EditorCharacterStylingInput,
): { styling: EditorCharacterStylingInput; unanimous: boolean } => {
  const votes: EditorCharacterStylingInput[] = []
  const walk = (node: Element, folded: EditorCharacterStylingInput): void => {
    for (const child of node.childNodes) {
      if (child.nodeType === Node.TEXT_NODE) {
        if (tinyMceText(child.nodeValue ?? '').trim() !== '') votes.push(folded)
      } else if (child.nodeType === Node.ELEMENT_NODE) {
        const element = child as Element
        if (!isHiddenElement(element))
          walk(element, foldCharacterStyling(folded, element))
      }
    }
  }
  walk(el, base)
  if (votes.length === 0) return { styling: base, unanimous: true }
  const first = stylingKey(votes[0])
  return votes.every((vote) => stylingKey(vote) === first)
    ? { styling: votes[0], unanimous: true }
    : { styling: base, unanimous: false }
}

/**
 * The alignment and line height a template's inner paragraphs painted in,
 * when they all agree; a block-level field takes it over its own.
 */
export const unanimousParagraphBlockStyling = (
  el: Element,
  fontSizePt: number,
): EditorBlockStylingInput | undefined => {
  const votes = [...el.querySelectorAll('p')].map((p) =>
    foldBlockStyling(undefined, p, fontSizePt),
  )
  if (votes.length === 0) return undefined
  const first = stylingKey(votes[0])
  return votes.every((vote) => stylingKey(vote) === first)
    ? votes[0]
    : undefined
}

/** A background that paints: a highlight, which no run can carry. */
const paintsBackground = (css: Map<string, string>): boolean => {
  const value = (css.get('background-color') ?? css.get('background') ?? '')
    .trim()
    .toLowerCase()
  return (
    value !== '' &&
    value !== 'transparent' &&
    value !== 'none' &&
    !CSS_WIDE_KEYWORDS.has(value)
  )
}

/** How many elements under `root` paint a background behind their text. */
export const tinyMceHighlightCount = (root: Element): number =>
  [...root.querySelectorAll('[style]')].filter((el) =>
    paintsBackground(inlineDeclarations(el)),
  ).length
